import React from "react";
import { Flame, HeartPulse, HardHat, Shield, AlertTriangle, CheckCircle2 } from "lucide-react";

/**
 * Le figure della sicurezza che ogni cantiere deve avere presidiate.
 * Si riconoscono dagli attestati in corso di validità: un corso scaduto non
 * copre la figura, quindi l'icona sparisce da sola alla scadenza.
 */
export const FIGURE = {
  primo_soccorso: { nome: "Primo Soccorso", icona: HeartPulse, colore: "text-emerald-600", sfondo: "bg-emerald-100" },
  antincendio: { nome: "Antincendio", icona: Flame, colore: "text-red-600", sfondo: "bg-red-100" },
  preposto: { nome: "Preposto", icona: HardHat, colore: "text-amber-600", sfondo: "bg-amber-100" },
  rls: { nome: "RLS", icona: Shield, colore: "text-[#0067b2]", sfondo: "bg-blue-100" },
};

export const ORDINE_FIGURE = ["primo_soccorso", "antincendio", "preposto", "rls"];

/** Icone delle figure possedute, da mettere accanto al nome. */
export function IconeFigure({ figure, dimensione = "w-6 h-6", className = "" }) {
  if (!figure?.length) return null;
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      {ORDINE_FIGURE.filter(f => figure.includes(f)).map(f => {
        const meta = FIGURE[f];
        const Icona = meta.icona;
        return (
          <span key={f} title={meta.nome}
                className={`${dimensione} ${meta.sfondo} rounded flex items-center justify-center flex-shrink-0`}>
            <Icona className={`w-3.5 h-3.5 ${meta.colore}`} />
          </span>
        );
      })}
    </span>
  );
}

/** Versione minuscola per gli elenchi fitti, come il selettore del personale. */
export function PallineFigure({ figure }) {
  if (!figure?.length) return null;
  return (
    <span className="inline-flex items-center gap-0.5 flex-shrink-0">
      {ORDINE_FIGURE.filter(f => figure.includes(f)).map(f => {
        const meta = FIGURE[f];
        const Icona = meta.icona;
        return <Icona key={f} title={meta.nome} className={`w-3 h-3 ${meta.colore}`} />;
      })}
    </span>
  );
}

/**
 * Riepilogo della copertura su una squadra: dice al tecnico, mentre sceglie,
 * se il cantiere resta scoperto su una delle figure obbligatorie.
 */
export function CoperturaSicurezza({ dipendentiSelezionati }) {
  const coperte = new Set();
  for (const d of dipendentiSelezionati || []) {
    for (const f of d.figure_sicurezza || []) coperte.add(f);
  }
  // L'RLS è aziendale, non serve presidiarlo su ogni singolo cantiere.
  const daPresidiare = ["primo_soccorso", "antincendio", "preposto"];
  const mancanti = daPresidiare.filter(f => !coperte.has(f));
  const completa = mancanti.length === 0;

  return (
    <div className={`rounded border p-3 ${completa
      ? "border-emerald-300 bg-emerald-50" : "border-amber-300 bg-amber-50"}`}
         data-testid="copertura-sicurezza">
      <div className="flex items-start gap-2">
        {completa
          ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
          : <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
        <div className="min-w-0 flex-1">
          <div className={`font-semibold text-sm ${completa ? "text-emerald-800" : "text-amber-900"}`}>
            {completa
              ? "Squadra presidiata su tutte le figure di sicurezza"
              : `Squadra scoperta su ${mancanti.length} figur${mancanti.length === 1 ? "a" : "e"}`}
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {daPresidiare.map(f => {
              const meta = FIGURE[f];
              const Icona = meta.icona;
              const ok = coperte.has(f);
              return (
                <span key={f}
                      className={`text-xs px-2 py-1 rounded flex items-center gap-1.5 ${
                        ok ? "bg-white border border-emerald-200" : "bg-white border border-amber-300 opacity-60"}`}>
                  <Icona className={`w-3.5 h-3.5 ${ok ? meta.colore : "text-slate-400"}`} />
                  {meta.nome}
                  {ok ? <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      : <span className="text-amber-700 font-semibold">manca</span>}
                </span>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
